import {
  useCallback,
  useEffect,
  useState,
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
} from 'react';

/**
 * ~40-line client router.
 *
 * react-router would be ~20 KB for what this site needs: five flat routes and
 * one parameterised one. The Worker rewrites unmatched paths to index.html, so
 * real URLs (not hashes) work on refresh and when shared.
 */

const listeners = new Set<() => void>();

function notify() {
  for (const fn of listeners) fn();
}

export function navigate(to: string, replace = false): void {
  if (to === window.location.pathname + window.location.search) return;
  if (replace) window.history.replaceState({}, '', to);
  else window.history.pushState({}, '', to);
  window.scrollTo({ top: 0 });
  notify();
}

export function useRoute(): string {
  const [path, setPath] = useState(() => window.location.pathname);

  useEffect(() => {
    const update = () => setPath(window.location.pathname);
    listeners.add(update);
    window.addEventListener('popstate', update);
    return () => {
      listeners.delete(update);
      window.removeEventListener('popstate', update);
    };
  }, []);

  return path;
}

interface LinkProps {
  to: string;
  className?: string;
  children: ReactNode;
  title?: string;
  style?: CSSProperties;
}

export function Link({ to, className, children, title, style }: LinkProps) {
  const onClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      // Let the browser handle modified clicks so "open in new tab" still works.
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }
      e.preventDefault();
      navigate(to);
    },
    [to],
  );

  return (
    <a href={to} onClick={onClick} className={className} title={title} style={style}>
      {children}
    </a>
  );
}

/** Matches `/teams/3` against `/teams/:id`, returning the captured segment. */
export function matchParam(path: string, prefix: string): string | null {
  if (!path.startsWith(`${prefix}/`)) return null;
  const rest = path.slice(prefix.length + 1);
  return rest && !rest.includes('/') ? decodeURIComponent(rest) : null;
}

/**
 * Matches a fixed number of segments, e.g. `/recap/2025/7` against
 * `/recap/:season/:week`.
 *
 * Exact on the count rather than lenient: a trailing segment would otherwise be
 * silently swallowed and render a page for the wrong week, which is worse than
 * a 404 because nothing about it looks wrong.
 */
export function matchSegments(path: string, prefix: string, count: number): string[] | null {
  if (!path.startsWith(`${prefix}/`)) return null;
  const parts = path.slice(prefix.length + 1).split('/');
  if (parts.length !== count || parts.some((p) => p === '')) return null;
  return parts.map(decodeURIComponent);
}
